import React, { useCallback, useEffect, useMemo, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { shallow } from 'zustand/shallow'
import { useTranslation } from 'react-i18next'
import { useApiErrorMessage } from '../../i18n/useApiErrorMessage'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import CustomBreadcrumbs from '../CustomBreadcrumbs/CustomBreadcrumbs'
import HtmlTooltip from '../HtmlTooltip/HtmlTooltip'
import InformationModal from '../Modals/ConfirmationModal/InformationModal'
import Layout from '../Layout/Layout'

import { MaterialIcons } from '../../icons/Material/MaterialIcons'

import {
  addCompanyNovelty,
  getCompanyNoveltiesByPeriodId,
  getCompanyNoveltiesTypes,
  getCompanyPeriodsTypes,
} from '../../services/CompaniesService'

import { downloadNoveltyFile, getCloserPeriods } from '../../services/ParametersService'

import { useEmpresaStore } from '../../store/empresaStore'
import { useParametrosStore } from '../../store/parametrosStore'
import { useUserStore } from '../../store/userStore'

import { reorderPeriods } from '../../utils/reorderPeriods'

import {
  CompanyNoveltiesType,
  CompanyPeriodsType,
  TipoPeriodoRowsType,
} from '../../types'

import {
  computeSuggestedPriority,
  getInterfaceCode,
  hasTriggerNovelty,
  TYPE_NOTICE_MESSAGES,
  validateNoveltyFileName,
} from './consts/crear-novedad-consts'

const TooltipMsg = () => {
  const { t } = useTranslation('novedades')
  return <Typography color="inherit">{t('crear.tooltipPriority')}</Typography>
}

const CrearNovedad = () => {
  const { t } = useTranslation(['novedades', 'common', 'validation'])
  const getApiErrorMessage = useApiErrorMessage()

  const links: any[] = [
    { id: '1', value: t('list.breadcrumb', { ns: 'novedades' }), isLink: true, to: '/novedades' },
    { id: '2', value: t('crear.breadcrumbCrear', { ns: 'novedades' }), isLink: false },
  ]
  const [openInformationModal, setOpenInformationModal] = useState<boolean>(false)
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [clickCancelar, setClickCancelar] = useState<boolean>(false)
  const [clickGuardar, setClickGuardar] = useState<boolean>(false)
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [downloadFile, setDownlaodFile] = useState<any>(null)
  const [empresaTipoNovedades, setEmpresaTipoNovedades] = useState<any[]>([])
  const [empresaPeriodos, setEmpresaPeriodos] = useState<CompanyPeriodsType[]>([])
  const [actualEmpresaNovedades, setActualEmpresaNovedades] = useState<any[]>([])
  const [disabledOnSubmit, setDisabledOnSubmit] = useState<boolean>(false)
  const [loadingPeriodos, setLoadingPeriodos] = useState<boolean>(false)
  const [periodNovelties, setPeriodNovelties] = useState<CompanyNoveltiesType[]>([])

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { getAccessTokenSilently } = useAuth0()

  // Prefill de "Subir nuevamente": si vienen estos query params, precargamos el
  // formulario y desactivamos los efectos que auto-seleccionan período/tipo/prioridad.
  const prefillName = searchParams.get('name')
  const prefillNoveltyTypeId = searchParams.get('novelty_type_id')
  const prefillPeriodId = searchParams.get('period_id')
  const prefillPriority = searchParams.get('priority')
  // Id de la novedad en Error que esta reemplaza (para que el backend la cancele).
  const prefillReplaces = searchParams.get('replaces')
  const isPrefilled = prefillName !== null && prefillNoveltyTypeId !== null
  const setAllCompanyNovedadesType = useParametrosStore(
    state => state.setAllCompanyNovedadesType,
  )
  const { empresaId } = useEmpresaStore(({ empresaId }) => ({ empresaId }), shallow)
  const isBDOAdmin = useUserStore(state => state.user?.role === 2)

  const handleOpenModal = () => setOpenInformationModal(true)
  const handleCloseModal = () => {
    setOpenInformationModal(false)
    setOpenErrorModal(false)
    setClickCancelar(false)
    setClickGuardar(false)
    setErrorMessage('')
  }

  const handleClickDirtyCancelar = () => {
    setIsDirty(true)
    setClickGuardar(false)
    setClickCancelar(true)
    handleOpenModal()
  }

  const handleClickGuardar = () => {
    setIsDirty(false)
    setClickCancelar(false)
    setClickGuardar(true)
    handleOpenModal()
  }

  const handleDeshacerCancelar = () => {
    formik.resetForm()
    navigate(-1)
  }

  const handleSaveButton = () => formik.submitForm()

  const getCloserPeriod = (unOrderedPeriods: CompanyPeriodsType[]) => {
    // No autoseleccionar período si venimos de "Subir nuevamente" (ya viene precargado).
    if (isPrefilled) return
    if (unOrderedPeriods.length > 0) {
      const sortedPeriods = reorderPeriods([...unOrderedPeriods])
      formik.setFieldValue('period_id', sortedPeriods[0].id)
    }
  }

  const downloadArch = async () => {
    const token = await getAccessTokenSilently()
    const result = await downloadNoveltyFile(token, downloadFile.ref)

    const link = document.createElement('a')
    const url = window.URL.createObjectURL(new Blob([result.data]))
    link.href = url
    link.setAttribute('download', downloadFile.name)
    document.body.appendChild(link)
    link.dispatchEvent(new MouseEvent('click'))
    link.parentNode?.removeChild(link)
  }

  const formik = useFormik({
    initialValues: {
      name: prefillName ?? '',
      period_id: prefillPeriodId ?? '',
      novelty_type_id: prefillNoveltyTypeId ?? '',
      fileName: '',
      file: '',
      urgent: false,
      priority: prefillPriority !== null ? Number(prefillPriority) : 0,
    },
    validationSchema: Yup.object({
      name: Yup.string().max(55).required(t('novelty.nameRequired', { ns: 'validation' })),
      priority: Yup.number()
        .min(0)
        .required(t('novelty.priorityRequired', { ns: 'validation' })),
      period_id: Yup.string()
        .max(255)
        .required(t('novelty.periodRequired', { ns: 'validation' })),
      fileName: Yup.mixed()
        .required(t('novelty.fileRequired', { ns: 'validation' }))
        .test(
          'filename-matches-novelty-type',
          t('novelty.fileNameTypeMismatch', { ns: 'validation' }),
          function (value) {
            if (!value) return true
            const code = getInterfaceCode(this.parent.novelty_type_id, empresaTipoNovedades)
            return validateNoveltyFileName(String(value), code)
          },
        ),
      novelty_type_id: Yup.string()
        .max(255)
        .required(t('novelty.typeRequired', { ns: 'validation' })),
    }),
    onSubmit: async data => {
      setDisabledOnSubmit(true)
      const finalData: Record<string, any> = {
        file: data.file,
        name: data.name,
        period_id: data.period_id,
        company_id: empresaId,
        novelty_type_id: findNoveltyTypeId(data.novelty_type_id),
        urgent: data.urgent,
        priority: data.priority,
      }
      if (prefillReplaces) {
        finalData.replaces = prefillReplaces
      }

      const token = await getAccessTokenSilently()
      addCompanyNovelty(token, empresaId, finalData)
        .then(() => {
          setOpenErrorModal(false)
          setErrorMessage('')
          setTimeout(() => {
            handleCloseModal()
            handleDeshacerCancelar()
          }, 4000)
        })
        .catch(e => {
          console.error(e.response.data.error)
          setDisabledOnSubmit(false)
          setOpenErrorModal(true)
          setErrorMessage(getApiErrorMessage(e))
        })
    },
  })

  const handleUploadFile = (file: any) => {
    formik.setValues({ ...formik.values, file, fileName: file.name })
    formik.setFieldTouched('fileName', true, false)
  }

  const handleChangePriority = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value)
    if (value >= 0) formik.setFieldValue('priority', value)
  }

  const findNoveltyTypeId = (noveltyTypeId: string): string => {
    return empresaTipoNovedades.length > 0
      ? empresaTipoNovedades.find(eTN => eTN.id === noveltyTypeId)?.id || ''
      : ''
  }

  const handleChangeNoveltyType = (e: React.ChangeEvent<HTMLInputElement>) => {
    const downloadFileRef = actualEmpresaNovedades.find(
      aEN => aEN.id === e.target.value,
    )?.file
    formik.setFieldValue('novelty_type_id', findNoveltyTypeId(e.target.value))
    setDownlaodFile(downloadFileRef)
  }

  const getActualEmpresaNovedadesType = useCallback(async () => {
    const token = await getAccessTokenSilently()
    try {
      const result = await getCompanyNoveltiesTypes(token, empresaId)
      const sortedNovelties = result.data.sort((a, b) =>
        a.novelty_type.name.localeCompare(b.novelty_type.name),
      )
      setActualEmpresaNovedades(sortedNovelties)
      setAllCompanyNovedadesType(sortedNovelties)
    } catch (e: any) {
      console.log('error', e.response?.data?.error)
      setActualEmpresaNovedades([])
      setAllCompanyNovedadesType([])
    }
  }, [])

  function getSanitizedPeriodsByCompanyPeriodTypes(
    periodTypes: TipoPeriodoRowsType[],
    periods: CompanyPeriodsType[],
  ): CompanyPeriodsType[] {
    // Crear un Set de IDs de periodTypes activos para búsqueda eficiente
    const activePeriodTypeIds = new Set(periodTypes.map(pt => pt.id))

    // Filtrar períodos que tengan un periodType activo en la empresa
    return periods.filter(period => activePeriodTypeIds.has(period.period_type_id))
  }

  const obtenerCloserPeriods = useCallback(async () => {
    const token = await getAccessTokenSilently()
    try {
      setLoadingPeriodos(true)
      const periodsResult = await getCloserPeriods(token, empresaId)
      const periodTypesResult = await getCompanyPeriodsTypes(token, empresaId)

      const sanitizedPeriods = getSanitizedPeriodsByCompanyPeriodTypes(
        periodTypesResult.data,
        periodsResult.data,
      )

      setEmpresaPeriodos(sanitizedPeriods)
      getCloserPeriod(sanitizedPeriods)
    } catch (e: any) {
      console.log('error', e.response?.data?.error)
    } finally {
      setLoadingPeriodos(false)
    }
  }, [])

  // --- Priority auto-assign logic ---

  const fetchPeriodNovelties = useCallback(
    async (periodId: string) => {
      if (!periodId || !empresaId) {
        setPeriodNovelties([])
        return
      }
      try {
        const token = await getAccessTokenSilently()
        const result = await getCompanyNoveltiesByPeriodId(token, empresaId, periodId)
        setPeriodNovelties(result.data)
      } catch {
        setPeriodNovelties([])
      }
    },
    [empresaId, getAccessTokenSilently],
  )

  const selectedInterfaceCode = useMemo(
    () => getInterfaceCode(formik.values.novelty_type_id, empresaTipoNovedades),
    [formik.values.novelty_type_id, empresaTipoNovedades],
  )

  const triggerInPeriod = useMemo(
    () => hasTriggerNovelty(periodNovelties, empresaTipoNovedades),
    [periodNovelties, empresaTipoNovedades],
  )

  const suggestedPriority = useMemo(
    () => computeSuggestedPriority(selectedInterfaceCode, triggerInPeriod),
    [selectedInterfaceCode, triggerInPeriod],
  )

  const typeNoticeMessage = useMemo(
    () => (selectedInterfaceCode !== null ? TYPE_NOTICE_MESSAGES[selectedInterfaceCode] : undefined),
    [selectedInterfaceCode],
  )

  useEffect(() => {
    fetchPeriodNovelties(formik.values.period_id)
  }, [formik.values.period_id, fetchPeriodNovelties])

  useEffect(() => {
    // No sobreescribir la prioridad precargada desde "Subir nuevamente".
    if (isPrefilled) return
    formik.setFieldValue('priority', suggestedPriority ?? 0)
  }, [suggestedPriority])

  useEffect(() => {
    document.title = `${window._env_.REACT_APP_EMPRESA_NAME} - ${t('crear.breadcrumbCrear', { ns: 'novedades' })}`
  }, [])

  useEffect(() => {
    if (actualEmpresaNovedades.length > 0) {
      if (isPrefilled) {
        // Mantener el tipo precargado; solo resolver el archivo de plantilla asociado.
        const match = actualEmpresaNovedades.find(
          n => n.id === prefillNoveltyTypeId,
        )
        setDownlaodFile((match ?? actualEmpresaNovedades[0]).file)
      } else {
        formik.setFieldValue('novelty_type_id', actualEmpresaNovedades[0].id)
        setDownlaodFile(actualEmpresaNovedades[0].file)
      }
      setEmpresaTipoNovedades(actualEmpresaNovedades)
    }
  }, [actualEmpresaNovedades])

  useEffect(() => {
    obtenerCloserPeriods()
    getActualEmpresaNovedadesType()
  }, [])

  return (
    <Layout title={t('crear.breadcrumbCrear', { ns: 'novedades' })} noIcon>
      <CustomBreadcrumbs icon="PollIcon" links={links} />
      <Grid container marginTop="12px" borderRadius="6px">
        <Grid bgcolor="white" item xs={8} borderRadius="6px">
          <Box
            sx={{
              borderRadius: '6px',
              display: 'flex',
              backgroundColor: 'white',
              height: '100%',
              width: '100%',
              paddingRight: '18px',
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Box
                id="create-novelty-form"
                component="form"
                autoComplete="off"
                borderRadius="6px"
                sx={{ backgroundColor: 'white', height: '100%' }}
                onSubmit={formik.handleSubmit}
              >
                <Grid container>
                  <Grid item xs={6} sx={{ pt: '18px', pl: '18px' }}>
                    <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                      <InputLabel sx={{ color: 'black' }} htmlFor="nombre-input">
                        {t('crear.fieldName', { ns: 'novedades' })}
                      </InputLabel>
                    </Box>
                    <TextField
                      id="nombre-input"
                      error={Boolean(formik.touched.name && formik.errors.name)}
                      fullWidth
                      helperText={formik.touched.name && formik.errors.name}
                      name="name"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      type="text"
                      value={formik.values.name}
                      variant="filled"
                      placeholder={t('crear.fieldNamePlaceholder', { ns: 'novedades' })}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          borderRadius: '6px',
                          '.MuiInputBase-input': {
                            paddingBottom: '16px',
                            paddingTop: '16px',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Grid container alignItems="center">
                      <Grid item xs={6} sx={{ pt: '18px', pl: '18px' }}>
                        <Box
                          display="flex"
                          justifyContent="flex-start"
                          marginBottom="4px"
                        >
                          <InputLabel sx={{ color: 'black' }} htmlFor="type-select">
                            {t('crear.fieldType', { ns: 'novedades' })}
                          </InputLabel>
                        </Box>
                        <TextField
                          id="type-select"
                          select
                          error={Boolean(
                            formik.touched.novelty_type_id &&
                            formik.errors.novelty_type_id,
                          )}
                          fullWidth
                          helperText={
                            formik.touched.novelty_type_id &&
                            formik.errors.novelty_type_id
                          }
                          name="novelty_type_id"
                          onBlur={formik.handleBlur}
                          onChange={handleChangeNoveltyType}
                          value={formik.values.novelty_type_id}
                          variant="filled"
                          InputProps={{
                            disableUnderline: true,
                            sx: {
                              borderRadius: '6px',
                              '.MuiInputBase-input': {
                                paddingBottom: '16px',
                                paddingTop: '16px',
                                display: 'flex',
                                justifyContent: 'flex-start',
                              },
                            },
                          }}
                        >
                          {empresaTipoNovedades.map(empresaTipoNovedad => (
                            <MenuItem
                              key={empresaTipoNovedad.id}
                              value={empresaTipoNovedad.id}
                            >
                              {`${empresaTipoNovedad.novelty_type.code} - ${empresaTipoNovedad.novelty_type.name}`}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={6} sx={{ pt: '48px', pl: '18px' }}>
                        <Box
                          display="flex"
                          justifyContent="flex-start"
                          marginBottom="4px"
                        >
                          <Button
                            color="primary"
                            variant="contained"
                            startIcon={<MaterialIcons.FileDownloadIcon />}
                            onClick={() => downloadArch()}
                            sx={{ height: '100%' }}
                          >
                            {t('actions.download', { ns: 'common' })}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={6} sx={{ pt: '18px', pl: '18px' }}>
                    <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                      <InputLabel sx={{ color: 'black' }} htmlFor="periodo-select">
                        {t('crear.fieldPeriod', { ns: 'novedades' })}
                      </InputLabel>
                    </Box>
                    <TextField
                      id="periodo-select"
                      select
                      error={Boolean(formik.touched.period_id && formik.errors.period_id)}
                      fullWidth
                      helperText={
                        loadingPeriodos
                          ? t('crear.loadingPeriods', { ns: 'novedades' })
                          : empresaPeriodos.length === 0
                            ? t('crear.noPeriodsActive', { ns: 'novedades' })
                            : formik.touched.period_id && formik.errors.period_id
                      }
                      name="period_id"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.period_id}
                      variant="filled"
                      disabled={loadingPeriodos || empresaPeriodos.length === 0} // bloquea mientras carga o si no hay periodos
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          borderRadius: '6px',
                          '.MuiInputBase-input': {
                            paddingBottom: '16px',
                            paddingTop: '16px',
                            display: 'flex',
                            justifyContent: 'flex-start',
                          },
                        },
                      }}
                    >
                      {loadingPeriodos ? (
                        <MenuItem value="">
                          <em>{t('crear.loading', { ns: 'novedades' })}</em>
                        </MenuItem>
                      ) : (
                        empresaPeriodos.map(periodo => (
                          <MenuItem key={periodo.id} value={periodo.id}>
                            {periodo.name}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} sx={{ pt: '18px', pl: '18px' }}>
                    <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                      <InputLabel sx={{ color: 'black' }} htmlFor="prioridad-input">
                        {t('crear.fieldPriority', { ns: 'novedades' })}
                      </InputLabel>
                    </Box>
                    <HtmlTooltip title={<TooltipMsg />} arrow>
                      <TextField
                        id="prioridad-input"
                        error={Boolean(formik.touched.priority && formik.errors.priority)}
                        fullWidth
                        helperText={formik.touched.priority && formik.errors.priority}
                        name="priority"
                        onBlur={formik.handleBlur}
                        onChange={handleChangePriority}
                        type="number"
                        value={formik.values.priority}
                        variant="filled"
                        placeholder={t('crear.fieldPriorityPlaceholder', { ns: 'novedades' })}
                        inputProps={{
                          min: 0,
                        }}
                        InputProps={{
                          disableUnderline: true,
                          readOnly: !isBDOAdmin,
                          sx: {
                            borderRadius: '6px',
                            ...(!isBDOAdmin && { backgroundColor: 'rgba(0,0,0,0.12)' }),
                            '.MuiInputBase-input': {
                              paddingBottom: '16px',
                              paddingTop: '16px',
                            },
                          },
                        }}
                      />
                    </HtmlTooltip>
                    {typeNoticeMessage && (
                      <Alert severity="info" sx={{ mt: 1, mb: 0, py: 0.5 }}>
                        {typeNoticeMessage}
                      </Alert>
                    )}
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={6} sx={{ pt: '18px', pl: '18px' }}>
                    <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                      <InputLabel sx={{ color: 'black' }} htmlFor="novedad-input">
                        {t('crear.fieldNovelty', { ns: 'novedades' })}
                      </InputLabel>
                    </Box>
                    <TextField
                      id="novedad-input"
                      error={Boolean(formik.touched.fileName && formik.errors.fileName)}
                      fullWidth
                      helperText={formik.touched.fileName && formik.errors.fileName}
                      name="fileName"
                      value={formik.values.fileName}
                      variant="filled"
                      placeholder={t('crear.fieldNoveltyPlaceholder', { ns: 'novedades' })}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <HtmlTooltip title={<b>{t('actions.upload', { ns: 'common' })}</b>}>
                              <IconButton
                                aria-label={t('crear.uploadFileAria', { ns: 'novedades' })}
                                component="label"
                              >
                                <input
                                  hidden
                                  id="subir-plantilla-button"
                                  type="file"
                                  name="file"
                                  onChange={(e: any) =>
                                    handleUploadFile(e.currentTarget.files[0])
                                  }
                                  accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                />
                                <MaterialIcons.FileUploadIcon color="primary" />
                              </IconButton>
                            </HtmlTooltip>
                          </InputAdornment>
                        ),
                        readOnly: true,
                        disableUnderline: true,
                        sx: {
                          borderRadius: '6px',
                          '.MuiInputBase-input': {
                            paddingBottom: '16px',
                            paddingTop: '16px',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={2} sx={{ pb: '24px', pt: '8px', pl: '18px' }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="urgent"
                            checked={formik.values.urgent}
                            onChange={formik.handleChange}
                            inputProps={{
                              'aria-label': 'urgente-checkbox',
                            }}
                          />
                        }
                        label={t('crear.urgent', { ns: 'novedades' })}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
                <Grid container maxWidth="sm" paddingBottom="32px">
                  <Box sx={{ display: 'flex' }}>
                    <Button
                      sx={{ marginLeft: '18px' }}
                      variant="contained"
                      color="primary"
                      type="button"
                      onClick={handleClickGuardar}
                      disabled={
                        !formik.dirty ||
                        !formik.values.period_id ||
                        !formik.values.name ||
                        !formik.values.fileName ||
                        !formik.values.novelty_type_id ||
                        Boolean(formik.errors.fileName)
                      }
                    >
                      {t('actions.save', { ns: 'common' })}
                    </Button>

                    {formik.values.name || formik.values.fileName ? (
                      <Button
                        sx={{ marginLeft: '18px' }}
                        variant="contained"
                        color="error"
                        onClick={handleClickDirtyCancelar}
                      >
                        {t('actions.cancel', { ns: 'common' })}
                      </Button>
                    ) : (
                      <Button
                        sx={{ marginLeft: '18px' }}
                        variant="contained"
                        color="error"
                        onClick={() => navigate(-1)}
                      >
                        {t('actions.cancel', { ns: 'common' })}
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {openInformationModal && (
        <InformationModal
          title={
            isDirty
              ? t('crear.cancelChangesTitle', { ns: 'novedades' })
              : t('crear.createTitle', { ns: 'novedades' })
          }
          message={
            isDirty
              ? t('crear.cancelChangesMessage', { ns: 'novedades' })
              : t('crear.createMessage', { ns: 'novedades' })
          }
          openModal={openInformationModal}
          onClose={handleCloseModal}
          saveForm={handleSaveButton}
          isDirty={isDirty}
          clickCancelar={clickCancelar}
          handleDeshacerCancelar={handleDeshacerCancelar}
          guardar={clickGuardar}
          guardarMessage={t('crear.createConfirmMessage', { ns: 'novedades' })}
          clickEliminar={false}
          disabledOnSubmit={disabledOnSubmit}
        />
      )}

      {openErrorModal && (
        <InformationModal
          title={t('crear.errorTitle', { ns: 'novedades' })}
          message={errorMessage}
          onClose={handleCloseModal}
          openModal={openErrorModal}
          clickCancelar={false}
          clickEliminar={false}
          guardar={false}
          isDirty={false}
          errorMessage
        />
      )}
    </Layout>
  )
}
export default CrearNovedad
